import type {
    ApplicationsContent,
    ApplicationsTabsContent1,
    ApplicationsTabsContent2,
} from '../types/ApplicationsContent';
import { InternalLinkSectionElement } from '../types/SiteContentElement';

const emptyTabs1: ApplicationsTabsContent1 = {
    Overview: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
    Resources: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
    SimulationTools: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
};

const emptyTabs2: ApplicationsTabsContent2 = {
    Overview: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
    Products: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
    Datasheets: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
    Resources: {
        title: { kind: 'plainText' as const, key: '', content: '' },
    },
};

const emptyInternalLinkSection: InternalLinkSectionElement = {
    kind: 'internalLinkSection',
    key: '',
    title: '',
    text: '',
    links: [],
};

export function createApplicationsContentPlaceholder(): ApplicationsContent {
    return {
        common: {
            Datasheets: {
                title: { kind: 'plainText', key: '', content: '' },
            },
            Overview: {
                title: { kind: 'plainText', key: '', content: '' },
            },
            Products: {
                title: { kind: 'plainText', key: '', content: '' },
            },
            Resources: {
                title: { kind: 'plainText', key: '', content: '' },
            },
            SimulationTools: {
                title: { kind: 'plainText', key: '', content: '' },
            },
        },
        AerospaceDefenseAndMedical: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs1,
            temperatureSensorLinks: emptyInternalLinkSection,
            Aerospace: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                linksTitle: { kind: 'plainText', key: '', content: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
            Defense: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                linksTitle: { kind: 'plainText', key: '', content: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
            Avionics: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                linksTitle: { kind: 'plainText', key: '', content: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
            Medical: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
        },
        Automotive: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs1,
            overviewTitle: { kind: 'plainText', key: '', content: '' },
            overviewMainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
            temperatureSensorLinks: emptyInternalLinkSection,
            highlights: { kind: 'highlightsCollection', key: '', entries: [] },
            Automotive: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    powertrain: { kind: 'plainText', key: '', content: '' },
                    vehicleElectrification: { kind: 'plainText', key: '', content: '' },
                    adas: { kind: 'plainText', key: '', content: '' },
                    infotainment: { kind: 'plainText', key: '', content: '' },
                    safetyAndSecurity: { kind: 'plainText', key: '', content: '' },
                    bodyElectronics: { kind: 'plainText', key: '', content: '' },
                },
            },
            ADAS: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    radar: { kind: 'plainText', key: '', content: '' },
                    lidar: { kind: 'plainText', key: '', content: '' },
                    cameras: { kind: 'plainText', key: '', content: '' },
                    adaptiveLighting: { kind: 'plainText', key: '', content: '' },
                    ultraSonicSensors: { kind: 'plainText', key: '', content: '' },
                },
            },
            BodyElectronics: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    electricWaterPump: { kind: 'plainText', key: '', content: '' },
                    electricPowerSteering: { kind: 'plainText', key: '', content: '' },
                    wirelessCharging: { kind: 'plainText', key: '', content: '' },
                    windows: { kind: 'plainText', key: '', content: '' },
                    sunroof: { kind: 'plainText', key: '', content: '' },
                    wireHarness: { kind: 'plainText', key: '', content: '' },
                    seatControl: { kind: 'plainText', key: '', content: '' },
                    electricCompressor: { kind: 'plainText', key: '', content: '' },
                },
            },
            Infotainment: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    instrumentCluster: { kind: 'plainText', key: '', content: '' },
                    headUpDisplay: { kind: 'plainText', key: '', content: '' },
                    navigation: { kind: 'plainText', key: '', content: '' },
                    nightVision: { kind: 'plainText', key: '', content: '' },
                },
            },
            Powertrain: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    radiatorFan: { kind: 'plainText', key: '', content: '' },
                    transmissionControlUnit: { kind: 'plainText', key: '', content: '' },
                    fuelInjectionSystem: { kind: 'plainText', key: '', content: '' },
                    eTurboAndEBoost: { kind: 'plainText', key: '', content: '' },
                    exhaustGasRecirculation: { kind: 'plainText', key: '', content: '' },
                    fuelPump: { kind: 'plainText', key: '', content: '' },
                    throttleControl: { kind: 'plainText', key: '', content: '' },
                    ignitionSystem: { kind: 'plainText', key: '', content: '' },
                    engineControlUnit: { kind: 'plainText', key: '', content: '' },
                    variableValveTiming: { kind: 'plainText', key: '', content: '' },
                },
            },
            SafetyAndSecurity: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    keylessEntry: { kind: 'plainText', key: '', content: '' },
                    antiTheftGPS: { kind: 'plainText', key: '', content: '' },
                    airbagDeployment: { kind: 'plainText', key: '', content: '' },
                    autoDimmingMirror: { kind: 'plainText', key: '', content: '' },
                    windshieldWiperControl: { kind: 'plainText', key: '', content: '' },
                    obd2: { kind: 'plainText', key: '', content: '' },
                    telematicControlUnit: { kind: 'plainText', key: '', content: '' },
                    tpms: { kind: 'plainText', key: '', content: '' },
                },
            },
            VehicleElectrification: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    dcDcConverter: { kind: 'plainText', key: '', content: '' },
                    vehicleWirelessCharging: { kind: 'plainText', key: '', content: '' },
                    batteryManagementSystem: { kind: 'plainText', key: '', content: '' },
                    electricMotorGenerator: { kind: 'plainText', key: '', content: '' },
                    tractionInverter: { kind: 'plainText', key: '', content: '' },
                    onboardCharger: { kind: 'plainText', key: '', content: '' },
                },
            },
        },
        ComputingAndEnterpriseSystems: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs1,
            overviewTitle: { kind: 'plainText', key: '', content: '' },
            overviewMainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
            Computing: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    climateControl: { kind: 'plainText', key: '', content: '' },
                    upsBatteries: { kind: 'plainText', key: '', content: '' },
                    mainPowerDistribution: { kind: 'plainText', key: '', content: '' },
                    edgeComputing: { kind: 'plainText', key: '', content: '' },
                    generator: { kind: 'plainText', key: '', content: '' },
                    networkPowerChiller: { kind: 'plainText', key: '', content: '' },
                    apc: { kind: 'plainText', key: '', content: '' },
                    pcsAndNotebooks: { kind: 'plainText', key: '', content: '' },
                    server: { kind: 'plainText', key: '', content: '' },
                },
            },
            temperatureSensorLinks: emptyInternalLinkSection,
        },
        Consumer: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs1,
            overviewTitle: { kind: 'plainText', key: '', content: '' },
            overviewMainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
            HomeConsumer: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    homeAppliances: { kind: 'plainText', key: '', content: '' },
                    iot: { kind: 'plainText', key: '', content: '' },
                    homeTheater: { kind: 'plainText', key: '', content: '' },
                    portableElectronics: { kind: 'plainText', key: '', content: '' },
                    powerTools: { kind: 'plainText', key: '', content: '' },
                    peripheralsAndPrinters: { kind: 'plainText', key: '', content: '' },
                    gaming: { kind: 'plainText', key: '', content: '' },
                },
            },
            PortableConsumer: {
                title: { kind: 'plainText', key: '', content: '' },
                imageMapLinkTexts: {
                    wearables: { kind: 'plainText', key: '', content: '' },
                    smartphone: { kind: 'plainText', key: '', content: '' },
                    tablets: { kind: 'plainText', key: '', content: '' },
                },
            },
            temperatureSensorLinks: emptyInternalLinkSection,
        },
        Industrial: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs1,
            overviewTitle: { kind: 'plainText', key: '', content: '' },
            overviewMainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
            Industrial: {
                title: { kind: 'plainText', key: '', content: '' },
                powerSupplyType: {
                    lowVoltageDcDcLabel: { kind: 'plainText', key: '', content: '' },
                    acDcLabel: { kind: 'plainText', key: '', content: '' },
                },
                imageMapLinkTexts: {
                    gridInfrastructureAndMetering: { kind: 'plainText', key: '', content: '' },
                    renewableAndGreenEnergy: { kind: 'plainText', key: '', content: '' },
                    testAndMeasurement: { kind: 'plainText', key: '', content: '' },
                    evCharging: { kind: 'plainText', key: '', content: '' },
                    buildingAutomationAndFactoryAutomation: {
                        kind: 'plainText',
                        key: '',
                        content: '',
                    },
                    motorDrivesAndTransport: { kind: 'plainText', key: '', content: '' },
                    lightingAndDigitalSignage: { kind: 'plainText', key: '', content: '' },
                    hvac: { kind: 'plainText', key: '', content: '' },
                    powerSupply: { kind: 'plainText', key: '', content: '' },
                },
            },
            oilAndGas: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
            temperatureSensorLinks: emptyInternalLinkSection,
        },
        Telecommunications: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs1,
            FiveGAndWirelessInfrastructure: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
            FixedNetworkInfrastructure: {
                title: { kind: 'plainText', key: '', content: '' },
                mainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
                mainImage: { kind: 'image', key: '', imageUrl: '' },
                links: { kind: 'linkCollection', key: '', links: [] },
            },
        },
        TempSensingForElectronics: {
            pageTitle: { kind: 'plainText', key: '', content: '' },
            pageSubtitle: { kind: 'plainMultilineText', key: '', content: '' },
            pageTitleBackgroundImage: { kind: 'image', key: '', imageUrl: '' },
            tabs: emptyTabs2,
            overviewTitle: { kind: 'plainText', key: '', content: '' },
            overviewMainText: { kind: 'richText', key: '', content: '', sanitizedContent: '' },
            overviewMainImage: { kind: 'image', key: '', imageUrl: '' },
        },
    };
}
